import React, { useState, useEffect } from 'react';
import { doc, setDoc, updateDoc, collection, query, where, onSnapshot, getDoc, serverTimestamp } from "firebase/firestore";

import storageKeys from '../App/constants/storageKeys';
import { generateUUID } from '../utils/ids';
import { normalizeStatus } from '../utils/booking';
import { createBookingOnServer } from '../utils/createBooking';

import { db } from '../config/firebase';
import { useAuth } from './Auth.context';
export const AppContext = React.createContext({});

export const useApp = () => {
  return React.useContext(AppContext);
};

// const vkchquiz = JSON.parse(localStorage.getItem(storageKeys.VKCH_ITINERARY_DATA));

const generateItineraryEmailHtml = (itineraryData) => {
  const accommodationName = itineraryData?.bookings?.accommodation?.name || 'Your selected accommodation';
  const location = itineraryData.location || 'your destination';
  const vacationType = itineraryData.vacationtype || 'vacation';
  const typeTravelers = itineraryData.typeoftravelers || '';

  let dateStr = '';
  if (itineraryData.dates && itineraryData.dates.length === 2) {
    const startDate = new Date(itineraryData.dates[0].seconds * 1000).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
    const endDate = new Date(itineraryData.dates[1].seconds * 1000).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
    dateStr = `${startDate} - ${endDate}`;
  }

  const itineraryUrl = `http://localhost:3000/itinerary/${itineraryData.id}`;

  return `
    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #4a4a4a; max-width: 650px; margin: 0 auto; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 15px rgba(0,0,0,0.1); border: 1px solid #eaeaea;">
      <div style="background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%); color: white; padding: 30px 20px; text-align: center;">
        <h1 style="margin: 0; font-size: 28px; font-weight: 700; letter-spacing: 0.5px;">Your Trip is Ready! ✈️</h1>
        <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">We've finalized all the details for your upcoming adventure.</p>
      </div>
      <div style="padding: 40px 30px; background-color: #ffffff;">
        <p style="font-size: 16px; line-height: 1.6; margin-top: 0;">Hello there,</p>
        <p style="font-size: 16px; line-height: 1.6;">Great news! Your <strong>${vacationType}</strong> trip to the beautiful <strong>${location}</strong> has been successfully finalized. Here is a quick snapshot of your itinerary:</p>
        
        <div style="background-color: #f8f9fa; border-left: 4px solid #FF6B6B; padding: 20px; border-radius: 0 8px 8px 0; margin: 25px 0;">
          <h2 style="margin-top: 0; color: #333; font-size: 18px; margin-bottom: 15px;">Trip Highlights</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px 0; font-size: 20px; width: 30px;">🏨</td>
              <td style="padding: 8px 0; font-size: 15px;"><strong>Accommodation:</strong> ${accommodationName}</td>
            </tr>
            <tr>
              <td style="padding: 8px 0; font-size: 20px; width: 30px;">📅</td>
              <td style="padding: 8px 0; font-size: 15px;"><strong>Dates:</strong> ${dateStr}</td>
            </tr>
            <tr>
              <td style="padding: 8px 0; font-size: 20px; width: 30px;">👥</td>
              <td style="padding: 8px 0; font-size: 15px;"><strong>Travelers:</strong> ${typeTravelers}</td>
            </tr>
          </table>
        </div>
        
        <div style="text-align: center; margin: 40px 0;">
          <a href="${itineraryUrl}" style="display: inline-block; background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%); color: white; padding: 14px 30px; text-decoration: none; border-radius: 30px; font-weight: bold; font-size: 16px; text-transform: uppercase; letter-spacing: 1px; box-shadow: 0 4px 10px rgba(255, 107, 107, 0.3);">View Full Itinerary</a>
        </div>
        
        <p style="font-size: 15px; line-height: 1.6; color: #666; margin-bottom: 0;">If you have any questions or need further assistance, simply reply to this email. We're here to help!</p>
      </div>
      <div style="background-color: #f1f1f1; padding: 20px; text-align: center; font-size: 13px; color: #888;">
        <p style="margin: 0;">Happy Travels! 🌍</p>
        <p style="margin: 5px 0 0 0;"><strong>The Vhakacha Team</strong></p>
      </div>
    </div>
  `;
};
export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [serviceListing, setServiceProviders] = useState([]);
  // Specifically "the listings listener has reported once", as distinct from
  // `isLoading`, which several unrelated fetches toggle. /listing/:id must not
  // decide a listing is missing while the first snapshot is still in flight,
  // and an empty `serviceListing` cannot tell those two states apart.
  const [listingsLoaded, setListingsLoaded] = useState(false);
  const [serviceFilters, setServiceFilters] = useState([]);
  const [adverts, setAdverts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [itinerary] = useState(JSON.parse(localStorage.getItem(storageKeys.VKCH_ITINERARY_DATA)));
  const [itineraryList, setItineraryList] = useState([]);

  /**
   * Subscribes to published listings and returns the unsubscribe.
   *
   * Deliberately NOT async. An async function returns a Promise, so the
   * unsubscribe it produced could never be used as an effect cleanup — the
   * caller dropped it, and every auth change stacked another live listener
   * on top of the previous one, each still writing to `serviceListing`.
   *
   * The one-shot fetches it used to await (filters, bookings) are not part
   * of this subscription's lifecycle and are started by the caller instead.
   *
   * @returns {() => void} unsubscribe
   */
  function getAllServiceProviders() {
    setIsLoading(true);

    const q = query(collection(db, "listings"), where("status", "==", "PUBLISHED"));

    return onSnapshot(
      q,
      (querySnapshot) => {
        const providersArray = [];
        querySnapshot.forEach((doc) => {
          providersArray.push({ id: doc.id, booked: false, ...doc.data() });
        });

        setServiceProviders(providersArray);
        // Turn off loading once the first batch of data arrives
        setIsLoading(false);
        setListingsLoaded(true);
      },
      (error) => {
        console.error("Error listening to listings:", error);
        setIsLoading(false);
        // Set on failure too. A listener that errored is never going to
        // deliver, and leaving this false would hang /listing/:id on a
        // spinner forever instead of showing the not-found page.
        setListingsLoaded(true);
      }
    );
  }

  /**
   * Subscribes to live advertising placements and returns the unsubscribe.
   *
   * ⚠️ THE `where` IS MANDATORY, NOT AN OPTIMISATION. firestore.rules allows
   * reading an advert only when its status is ACTIVE, and Firestore requires
   * a list query to PROVE it satisfies the read rule. An unfiltered
   * `collection(db, 'advertisements')` is rejected outright with
   * "Missing or insufficient permissions" — it does NOT come back empty. So
   * dropping this filter does not degrade the carousel, it blanks it.
   *
   * Asserted in the provider app's tests/rules/advertising.mjs.
   *
   * The flight window (startAt/endAt) is applied in memory by selectAds:
   * Firestore cannot range-filter two fields in one query, and this
   * collection is tens of documents.
   */
  function getAdverts() {
    const q = query(collection(db, "advertisements"), where("status", "==", "ACTIVE"));

    return onSnapshot(
      q,
      (querySnapshot) => {
        setAdverts(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        // Non-fatal on purpose. MainCarousel falls back to its bundled house
        // slide, so a failure here costs the adverts, not the home page.
        console.error("Error listening to advertisements:", error);
        setAdverts([]);
      }
    );
  }

  async function getServiceFilters() {
    try {
      console.log("Getting service filters from Firestore");
      const docRef = doc(db, "service_provider_filters", "categories");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setServiceFilters(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching service filters:", error);
    }
  }

  async function getItineraryData() {
    setIsLoading(true);

    if (user) {
      const userId = user.uid;
      console.log("get Itinerary data ", userId);
      const q = query(collection(db, "itineraries"), where("uid", "==", userId));
      onSnapshot(q, (querySnapshot) => {
        const itineraryArray = [];
        querySnapshot.forEach((doc) => {
          itineraryArray.push(doc.data());
        });
        console.log("Itinerary data ", itineraryArray);
        setItineraryList(itineraryArray)
      });
    }

    setIsLoading(false);
  }

  async function createItinerary(item) {
    const userId = user?.uid;
    item.id = generateUUID();
    item["finalized"] = false;
    item["schedule"] = [];
    item["bookings"] = null;
    item["travelers"] = { "adults": 1, "children": 0, "infants": 0 }
    // Store locally for offline fallback
    localStorage.setItem(storageKeys.VKCH_ITINERARY_DATA, JSON.stringify(item));
    if (userId) {
      try {
        await setDoc(doc(db, "itineraries", item.id), { ...item, uid: userId });

      } catch (error) {
        console.error("Failed to save itinerary to Firestore:", error);
      }
    }
  }

  // Update only the accommodation field of an existing itinerary
  async function updateItineraryAccommodation(itineraryId, accommodation) {
    setIsLoading(true);
    try {
      await updateDoc(doc(db, "itineraries", itineraryId), { bookings: { accommodation } });
      // getItineraryData();
    } catch (error) {
      console.error("Failed to update accommodation in Firestore:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function saveItineraryTravelers(itineraryId, travelers) {
    setIsLoading(true);
    try {
      await updateDoc(doc(db, "itineraries", itineraryId), { travelers });
    } catch (error) {
      console.error("Failed to update travelers in Firestore:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function finalizeItinerary(itineraryId, finalized) {
    setIsLoading(true);
    try {
      const currentItinerary = itineraryList.find(i => i.id === itineraryId);
      if (!currentItinerary) {
        // Thrown, not logged-and-returned. A silent return resolved the
        // promise, so the caller could not tell a finalise that did nothing
        // from one that worked — and went on to tell the traveller's guests
        // the trip was confirmed.
        throw new Error("We could not find that itinerary. Please reload and try again.");
      }

      const accommodation = currentItinerary.bookings?.accommodation || null;
      let bookingId = null;

      if (accommodation && finalized) {
        // The itinerary embeds only a thin accommodation snapshot, which has
        // no serviceProviderId — without it the provider dashboard cannot see
        // the booking. Resolve the full listing so the server can find the
        // owning provider.
        const listing = await resolveListing(accommodation.id, accommodation);

        // The server writes it. The id is no longer minted here — the
        // booking's identity is decided by whoever actually creates the
        // document, and that is now createBooking.
        const created = await createBookingOnServer({
          listing,
          travelers: currentItinerary.travelers,
          dateRange: currentItinerary.dates,
          info: '',
          user,
          itineraryId,
        });
        bookingId = created.bookingId;
      }

      // Guarded: an itinerary finalized with no accommodation selected used
      // to dereference `currentItinerary.bookings.accommodation` here and
      // throw, leaving `finalized` unwritten.
      const bookingsUpdate = accommodation
        ? { accommodation: { ...accommodation, bookingId } }
        : currentItinerary.bookings ?? null;

      await updateDoc(doc(db, "itineraries", itineraryId), { finalized, bookings: bookingsUpdate });



      // Only send the email if it hasn't been finalized previously
      if (user && user.email && currentItinerary && !currentItinerary.finalized && finalized) {
        await setDoc(doc(collection(db, "mail")), {
          to: user.email,
          message: {
            subject: 'Your Vhakacha Itinerary is Finalised! 🎉',
            html: generateItineraryEmailHtml(currentItinerary)
          },
        });
        console.log("Email record added successfully!");
      }
      // getItineraryData();
    } catch (error) {
      // RETHROWN. This used to swallow every failure and resolve, so a caller
      // awaiting it could not tell success from failure. Once the itinerary
      // page began republishing to invited guests and emailing them a "your
      // trip is confirmed" copy on the strength of that promise resolving,
      // swallowing meant a failed finalise still told four people the trip was
      // settled. Logged as well, because the message a caller shows is not the
      // stack a developer needs.
      console.error("Failed to finalise itinerary:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Persists the generated day plan so it stops changing under the traveller.
   *
   * The schedule was recomputed from scratch on every mount, with each slot's
   * prose drawn at random — so the same itinerary described itself differently
   * on every visit. Nobody noticed while it was private. It became a real
   * problem the moment a copy of it could be published to other people and
   * emailed as final: guests saw the plan churn for no reason, and the "final
   * copy" was a draw of the dice the owner would never see again.
   *
   * Written once, the first time a plan is built for an itinerary that has
   * none. Never overwritten — regenerating would reintroduce exactly the churn
   * this removes.
   */
  async function saveItinerarySchedule(itineraryId, schedule) {
    if (!itineraryId || !Array.isArray(schedule) || !schedule.length) return;
    try {
      await updateDoc(doc(db, "itineraries", itineraryId), { schedule });
    } catch (error) {
      // Non-fatal: an unsaved schedule is the old behaviour, not a broken page.
      console.warn("saveItinerarySchedule: could not persist the day plan", error);
    }
  }

  async function deleteItinerary(itineraryId) {
    setIsLoading(true);
    try {
      console.log('deleting itinerary', itineraryId)
    } catch (error) {
      console.error("Failed to update accommodation in Firestore:", error);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Returns the full listing document for an id, preferring the already
   * loaded `serviceListing` cache and falling back to a direct read.
   * `fallback` is the thin snapshot an itinerary carries, used only when
   * the listing has since been unpublished or deleted.
   */
  async function resolveListing(listingId, fallback = null) {
    if (!listingId) return fallback;

    const cached = serviceListing.find((l) => l.id === listingId);
    if (cached) return cached;

    try {
      const snap = await getDoc(doc(db, "listings", listingId));
      if (snap.exists()) return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error("resolveListing: failed to read listing", listingId, error);
    }
    return fallback;
  }

  /**
   * Writes a canonical v2 booking. Callers must pass a document built by
   * `buildBooking()` — writing an ad-hoc object here is what produced the
   * two divergent booking schemas in the first place.
   */
  /**
   * Removed. Bookings are created by the server.
   *
   * This used to setDoc into `bookings` directly. That collection is now
   * `allow create: if false` for every client, so the write cannot succeed —
   * it fails with "Missing or insufficient permissions" at the moment a
   * traveller presses Submit.
   *
   * Leaving the doomed write in place was a mistake: it looked like a working
   * function and BookingForm kept calling it, so every booking failed in
   * production. Throwing here means a missed call site fails immediately, in
   * development, with the fix in the message.
   */
  async function addBooking() {
    throw new Error(
      'addBooking() has been removed — bookings are created by the server. ' +
        'Use createBookingOnServer() from utils/createBooking.'
    );
  }

  async function getAllBookings(id) {
    setIsLoading(true);
    console.log("get bookings: ", id);
    const q = query(collection(db, "bookings"), where("uid", "==", id));
    onSnapshot(q, (querySnapshot) => {
      const bookingData = [];
      querySnapshot.forEach((doc) => {
        bookingData.push(doc.data());
      });
      console.log("bookingData: ", bookingData);
      setBookings(bookingData)
    });
    setIsLoading(false);
  }

  async function getBooking(bookingId) {
    setIsLoading(true);
    try {
      const docRef = doc(db, "bookings", bookingId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        console.log("No such booking!");
        return null;
      }
    } catch (error) {
      console.error("Error getting booking:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  async function updateBooking(bookingId, updates) {
    setIsLoading(true);
    try {
      const payload = { ...updates, updatedAt: serverTimestamp() };
      // Keep the enum uppercase regardless of what a caller passes, so the
      // provider UI's === 'PENDING' comparisons keep matching.
      if (payload.status !== undefined) payload.status = normalizeStatus(payload.status);
      await updateDoc(doc(db, "bookings", bookingId), payload);
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // One-shot fetches, independent of the listener's lifecycle.
    getServiceFilters();
    if (user) getAllBookings(user.uid);

    // Both listeners are torn down together. This effect used to discard the
    // one it returned, leaving a live listener behind on every login and
    // logout.
    const unsubscribeListings = getAllServiceProviders();
    const unsubscribeAdverts = getAdverts();

    return () => {
      unsubscribeListings();
      unsubscribeAdverts();
    };
  }, [user]);

  return (
    <AppContext.Provider
      value={{
        isLoading,
        serviceListing,
        listingsLoaded,
        adverts,
        serviceFilters,
        itinerary,
        user,
        itineraryList,
        bookings,
        addBooking,
        getAllBookings,
        createItinerary,
        updateItineraryAccommodation,
        saveItineraryTravelers,
        getItineraryData,
        finalizeItinerary,
        saveItinerarySchedule,
        deleteItinerary,
        getBooking,
        updateBooking
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// export default AppProvider;